import moment from 'moment-timezone';
import Cookies from 'js-cookie';

import { getAdobeAuthToken } from '../helpers/Adobe';
import {
  getAPIUrl,
  getSubdomain,
} from '../helpers/App';

export const cookieParams = {
  domain: window.location.hostname,
  secure: window.location.protocol === 'https:',
  expires: 30,
};

export const getMiniCookieDomain = () => (
  process.env.MIX_APP_MINI_DOMAIN
);

const setAuthCookie = (name, value, params = cookieParams) => {
  Cookies.set(name, value, params);
  if (window.sp?.mini && window.sp?.appUrl) {
    Cookies.set(`${name}_${getSubdomain(window.sp.appUrl)}`, value, params);
  }
};

export const removeAuthCookie = (name, params = cookieParams) => {
  Cookies.remove(name, params);
  Cookies.remove(`${name}_${getSubdomain(window.sp?.appUrl)}`, params);
};

export const getAuthTokenName = () => {
  if (!window?.sp?.miniConfig?.integrationId) {
    return '';
  }
  if (window?.sp?.miniConfig?.integrationId.startsWith('adobe')) {
    return 'adobe-auth-token';
  }
  const suffix = window.sp.miniConfig.authCookieSuffix || '';
  return `${window?.sp?.miniConfig?.integrationId}-auth-token${suffix}`;
};

export const getAuthToken = () => {
  if (window.sp.mini && window.sp.miniConfig.authToken) return window.sp.miniConfig.authToken;

  if (!window?.sp?.miniConfig?.integrationId) {
    return '';
  }

  if (window.sp.miniConfig.integrationId.startsWith('adobe')) {
    if (process.env.MIX_NGROK_DEV_ENABLED) {
      return localStorage.getItem('adobe-auth-token');
    }
  }

  if (
    window.sp.miniConfig.integrationId === 'webflow' ||
    window.sp.miniConfig.integrationId === 'figma'
  ) {
    return localStorage.getItem(getAuthTokenName());
  }

  return '';
};

export const getAsyncAuthToken = async () => {
  if (window.sp.miniConfig.integrationId.startsWith('adobe')) {
    try {
      const token = await getAdobeAuthToken();
      return token;
    } catch (error) {
      return '';
    }
  }
  return '';
};

export const getAccessToken = () => (
  Cookies.get('sp_access_token')
);

export const getRefreshToken = () => (
  Cookies.get('sp_refresh_token')
);

export const getExpiration = () => (
  Cookies.get('sp_expiration')
);

export const isLoggedIn = () => (
  (getAccessToken() || getAuthToken()) || false
);

const saveTokens = (response) => {
  if (response.data) {
    if (response.data.access_token) {
      setAuthCookie('sp_access_token', response.data.access_token);
    }
    if (response.data.expires_in) {
      setAuthCookie('sp_expiration', moment().unix() + parseInt(response.data.expires_in));
    }
    if (response.data.refresh_token) {
      setAuthCookie('sp_refresh_token', response.data.refresh_token);
    }
  }
};

export const APIlogin = ({
  username,
  password,
  loginToken,
  jwtToken,
  googleCredential,
  g2faCode,
  codeChallenge,
  redirectUri,
  clientId,
  state,
  fsbc,
}) => (
  window.axios.post(getAPIUrl('/login'), {
    username,
    password,
    ...(loginToken && { login_token: loginToken }),
    ...(jwtToken && { jwt_token: jwtToken }),
    ...(googleCredential && { google_credential: googleCredential }),
    ...(g2faCode && { g2fa_code: g2faCode }),
    ...(codeChallenge && { code_challenge: codeChallenge }),
    ...(redirectUri && { redirect_uri: redirectUri }),
    ...(clientId && { client_id: clientId }),
    ...(state && { state }),
  }, {
    headers: {
      'user-timezone': moment.tz.guess(true),
      fsbc,
    },
  }).then((response) => {
    saveTokens(response);
    return response;
  })
);

export const APIrefreshToken = () => (
  window.axios.post(getAPIUrl('/refresh_token'), {
    refresh_token: Cookies.get('sp_refresh_token'),
  }, {
    headers: {
      'user-timezone': moment.tz.guess(true),
    },
  }).then((response) => {
    saveTokens(response);
    return response;
  })
);

export const APIlogout = () => (
  window.axios.post(getAPIUrl('/logout'))
    .then(response => (response))
    .catch(() => {
    })
);

export const APIpasswordReset = email => (
  window.axios.post(getAPIUrl('/password_reset'), { email })
    .then(response => (response))
);

export const APIpasswordChange = params => (
  window.axios.post(getAPIUrl('/password_change'), params)
    .then(response => (response))
);

export const APIgetAccountSwitchToken = clientUuid => (
  window.axios.get(getAPIUrl(`/clients/${clientUuid}/switch`))
    .then(response => (response.data))
);

export const APIauthorizeIntegration = (serviceId, code, state) => (
  window.axios.post(getAPIUrl(`/integrations/${serviceId}/oauth2`), { code, state })
    .then(response => (response.data))
);

export const APIdeauthorizeIntegration = serviceId => (
  window.axios.post(getAPIUrl(`/integrations/${serviceId}/disconnect`))
    .then(response => (response.data))
);
